import * as moment from 'moment';

import { Component, Input, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';

import {
  eventPartIsEmpty,
  EventPricingMatrix,
  EventType,
  NonRepeatingEvent,
  NonRepeatingEventPart
} from '../../../../../../../../models/event-type';
import { AccountService } from '../../../../../../../../services/account/account.service';
import { EventTypeService } from '../../../../../../../../services/event-type/event-type.service';
import { CapitanLocation, LocationArea } from '../../../../../../../../models/location';
import { LocationService } from '../../../../../../../../services/location/location.service';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { QuickAddOccurrencesModalComponent } from './modals/quick-add-occurrences-modal/quick-add-occurrences-modal.component';
import { getISO8601DateStringFromMoment, propExists } from '../../../../../../../../utils';
import { ScheduleType } from '../../../../../../../../models/event-type-enums';
import { TaxRate } from '../../../../../../../../models/tax-rate';
import { TaxRateService } from '../../../../../../../../services/tax-rate/tax-rate.service';
import { Membership } from '../../../../../../../../models/membership';
import { MembershipService } from '../../../../../../../../services/membership/membership.service';
import { MembershipPurchaseType, MembershipStatus } from '../../../../../../../../models/membership-enums';

@Component({
  selector: 'app-event-type-edit-schedule-non-repeating',
  templateUrl: './event-type-edit-schedule-non-repeating.component.html',
  styleUrls: ['./event-type-edit-schedule-non-repeating.component.css']
})
export class EventTypeEditScheduleNonRepeatingComponent implements OnInit {
  @Input()
  eventType: EventType;

  @Input()
  pricingMatrices: EventPricingMatrix[];

  @Input()
  locations: CapitanLocation[];

  public isNewEvent: boolean;
  public event: NonRepeatingEvent = {
    id: null,
    event_type_id: null,
    event_type_name: '',
    location_id: null,
    location_name: '',
    location_area_id: null,
    capacity: undefined,
    rolling_days: undefined,
    rolling_days_use_exact_date: false,
    booking_open_time: '00:00',
    pricing_matrix_id: null,
    tax_rate_id: null,
    event_parts: [],
    is_private: false,
    request_form_booking_id: null,
    private_notes: '',
    occurrence_name: '',
    customer_facing_name: '',
    benefit_membership_id: null,
    benefit_membership_name: '',
    benefit_membership_additional_days: 0,
    created_at: '',
    updated_at: ''
  };
  public bookingOpenDate: string;

  public locationAreaList: LocationArea[];
  public taxRateList: TaxRate[];
  public benefitMembershipList: Membership[];

  public loadingLocationAreas = false;
  public loadingTaxRates = true;
  public loadingBenefitMemberships = true;
  public loadingEvent = false;
  public loadingSave = false;

  public eventNotFound = false;
  public errorMessage = '';
  public errors: any = {};
  public serverError: number = null;

  public readonly ScheduleType = ScheduleType;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private accountService: AccountService,
    private eventTypeService: EventTypeService,
    private locationService: LocationService,
    private taxRateService: TaxRateService,
    private membershipService: MembershipService,
    private modalService: NgbModal
  ) {}

  ngOnInit() {
    const routeParams = this.route.snapshot.params;
    this.isNewEvent = !routeParams.hasOwnProperty('scheduleId');

    this.loadLocationAreas();
    this.loadTaxRates();
    this.loadBenefitMemberships();

    // Get the event to edit, if an ID was provided.
    if (!this.isNewEvent) {
      const eventIdInt = parseInt(routeParams.scheduleId, 10);
      if (isNaN(eventIdInt)) {
        this.eventNotFound = true;
        return;
      }

      this.loadEvent(eventIdInt);
    } else {
      this.event.event_type_id = this.eventType.id;
      this.event.event_type_name = this.eventType.name;
      this.event.location_id = this.accountService.getMyAccount().default_gym_id;

      // If this is a new event, start it off with one event part.
      this.addNewEventPart();

      // If this event type has pricing matrices, set the pricing matrix field
      // to the first one initially.
      if (this.pricingMatrices.length > 0) {
        this.event.pricing_matrix_id = this.pricingMatrices[0].id;
      }
    }
  }

  public loadLocationAreas() {
    this.loadingLocationAreas = true;

    this.serverError = null;

    this.locationService
      .getLocationAreas(
        1,
        100,
        {}
        )
      .then(response => { this.locationAreaList = response.results; })
      .catch(reason => {
        if (reason.status === 403) {
          this.accountService.logout();
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => { this.loadingLocationAreas = false; });
  }

  public loadTaxRates() {
    this.loadingTaxRates = true;

    this.taxRateService
      .getTaxRates(1, 100)
      .then(response => {
        this.taxRateList = response.results;
      })
      .catch(reason => {
        if (reason.status === 403) {
          this.accountService.logout();
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => { this.loadingTaxRates = false; });
  }

  public benefitMembershipRetired(id: number | null): boolean {
    if (id === null || id === undefined) {
      return false;
    }
    return !this.benefitMembershipList ||
      !this.benefitMembershipList.some(membership => membership.id === id);
  }

  public loadBenefitMemberships() {
    this.loadingBenefitMemberships = true;

    this.membershipService
      .getMemberships(
        1,
        3000,
        {
          status: MembershipStatus.active,
          purchase_type: MembershipPurchaseType.benefit,
        }
      )
      .then(response => {
        this.benefitMembershipList = response.results;
      })
      .catch(reason => {
        this.serverError = reason.status;
      })
      .finally(() => { this.loadingBenefitMemberships = false; });
  }

  public isLoading() {
    return this.loadingEvent || this.loadingSave;
  }

  public save() {
    this.loadingSave = true;

    this.errorMessage = '';
    this.errors = {};

    if (this.event.rolling_days_use_exact_date) {
      this.calculateRollingDaysFromBookingOpenDate();
    }

    let savePromise: Promise<NonRepeatingEvent>;
    if (this.isNewEvent) {
      savePromise = this.eventTypeService
        .addNonRepeatingEvent(this.eventType.id, this.event, null);
    } else {
      savePromise = this.eventTypeService
        .updateNonRepeatingEvent(this.event);
    }

    savePromise
      .then(savedEvent => {
        this.router.navigate([
          '/events/setup/edit/' + this.eventType.id.toString() + '/schedules'
        ]);
      })
      .catch(reason => {
        if (reason.status === 400) {
          this.errors = reason.error;
          this.errorMessage = 'Please correct the errors above and try again.';
        } else if (reason.status === 403) {
          if (this.isNewEvent) {
            this.errorMessage = 'You do not have permission to add events.';
          } else {
            this.errorMessage = 'You do not have permission to modify this event.';
          }
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => { this.loadingSave = false; });
  }

  public addNewEventPart() {
    const newEventPart: NonRepeatingEventPart = {
      start_time: '',
      start_date: '',
      duration_minutes: null,
      instructors: [],
      start_datetime_isoformat: '',
      end_datetime_isoformat: ''
    };

    if (this.event.event_parts.length > 0) {
      const bottomEventPart = this.event.event_parts[this.event.event_parts.length - 1];
      newEventPart.start_time = bottomEventPart.start_time;
      newEventPart.duration_minutes = bottomEventPart.duration_minutes;
      for (const instructor of bottomEventPart.instructors) {
        // Create a deep copy of the instructor and add it to the new event part.
        newEventPart.instructors.push({
          id: instructor.id,
          first_name: instructor.first_name,
          last_name: instructor.last_name,
          is_sub: false
        });
      }
    }

    this.event.event_parts.push(newEventPart);
  }

  public getLocationAreasForLocation(): LocationArea[] {
    if (this.loadingLocationAreas) { return []; }

    return this.locationAreaList.filter(locationArea => locationArea.gym_id === this.event.location_id);
  }

  public openQuickAddOccurrencesModal() {
    const modalRef = this.modalService.open(QuickAddOccurrencesModalComponent);

    modalRef.componentInstance.submitCallback = (eventParts: NonRepeatingEventPart[]) => {
      if (this.event.event_parts.length > 0) {
        const numEventParts = this.event.event_parts.length;
        if (eventPartIsEmpty(this.event.event_parts[numEventParts - 1])) {
          this.event.event_parts.splice(numEventParts - 1, 1);
        }
      }

      this.event.event_parts = this.event.event_parts.concat(eventParts);
    }
  }

  public handleRollingDaysUseExactDateChange() {
    if (this.event.rolling_days_use_exact_date) {
      this.calculateBookingOpenDateFromRollingDays();
    } else {
      this.calculateRollingDaysFromBookingOpenDate();
    }
  }

  public getEventTypeTaxRate(): string {
    if (this.eventType.tax_rate_name) {
      let taxRateText = this.eventType.tax_rate_name;
      if (this.taxRateList) {
        for (const taxRate of this.taxRateList) {
          if (taxRate.id === this.eventType.tax_rate_id) {
            taxRateText += ' - ' + taxRate.rate_percent + '%';
          }
        }
      }
      return taxRateText;
    }
    else { return 'No Tax Rate'; }
  }

  private loadEvent(eventId: number) {
    this.loadingEvent = true;

    this.eventTypeService
      .getNonRepeatingEvent(eventId)
      .then(event => {
        this.event = event;

        if (this.event.is_private) {
          this.eventNotFound = true;
        }

        if (this.event.rolling_days_use_exact_date) {
          this.calculateBookingOpenDateFromRollingDays();
        }
      })
      .catch(reason => {
        if (reason.status === 403) {
          this.accountService.logout();
        } else if (reason.status === 404) {
          this.eventNotFound = true;
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => { this.loadingEvent = false; });
  }

  private calculateRollingDaysFromBookingOpenDate() {
    if (!propExists(this.bookingOpenDate) || (this.bookingOpenDate === '')) {
      return;
    }

    const startDate = this.getEventStartDate();

    if (startDate === '') { return; }

    this.event.rolling_days = moment(startDate).diff(moment(this.bookingOpenDate), 'days');
  }

  private calculateBookingOpenDateFromRollingDays() {
    if (!propExists(this.event.rolling_days)) {
      return;
    }

    const startDate = this.getEventStartDate();

    if (startDate === '') { return; }

    this.bookingOpenDate = getISO8601DateStringFromMoment(
      moment(startDate).subtract(this.event.rolling_days, 'days')
    );
  }

  private getEventStartDate() {
    if (this.event.event_parts.length === 0) { return ''; }

    return this.event.event_parts
      .map(ep => ep.start_date)
      .filter(sd => sd !== '')
      .sort()[0];
  }
}
